import React, { useState, useEffect, Fragment, useMemo } from "react";
import axios from "axios";
import DashboardContainer from "../UI/DashboardContainer";
import { variables } from "../../Variables";
import theme from "../UI/Theme";
import BarChart from "../BarChart";
import { MyListItemText } from "../UI/styledComponents/StyledComponents";

//-----IMPORTS MUI ------------------------
import ListItem from "@mui/material/ListItem";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import ListItemText from "@mui/material/ListItemText";
import Avatar from "@mui/material/Avatar";
import { Divider } from "@mui/material";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import EqualizerIcon from '@mui/icons-material/Equalizer';
import List from "@mui/material/List";
import Box from "@mui/material/Box";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import Grid from "@mui/material/Grid";
import Button from "@mui/material/Button";
import TimelineIcon from '@mui/icons-material/Timeline';

//loader Imports--------------
import { SyncLoader } from "react-spinners";

function SalesByConsultantSummaryGraph(props) {
  const { tabInitialDate, tabFinalDate } = props;

  const [salesByConsultantsList, setSalesByConsultantsList] = useState([]);
  const [cancellationsByConsultantsList, setCancellationsByConsultantsList] = useState([]);
  const [isLoading1, setIsLoading1] = useState(false);
  const [isLoading2, setIsLoading2] = useState(false);
  const [showSalesGraph, setShowSalesGraph] = useState(true);

  const matches = useMediaQuery(theme.breakpoints.down("lg"));
  const matches3 = useMediaQuery(theme.breakpoints.down("sm"));

  const showSalesGraphHandler = (event) => {
    event.preventDefault();
    setShowSalesGraph((prev) => !prev);
  };

  // --- API CALLS ---
  const getSalesByConsultantSummary = (start, end) => {
    setIsLoading1(true);
    axios
      .get(`${variables.API_URL}/sales/salesbyconsultantsummary/${start}/${end}`, { withCredentials: true })
      .then((res) => {
        setSalesByConsultantsList(res.data.SalesByConsultantSummary || []);
        setIsLoading1(false);
      })
      .catch((error) => {
        console.error(error);
        setIsLoading1(false);
      });
  };

  const getCancellationsByConsultantSummary = (start, end) => {
    setIsLoading2(true);
    axios
      .get(`${variables.API_URL}/sales/cancellationsbyconsultantsummary/${start}/${end}`, { withCredentials: true })
      .then((res) => {
        setCancellationsByConsultantsList(res.data.cancellationsByConsultantSummary || []);
        setIsLoading2(false);
      })
      .catch((error) => {
        console.error(error);
        setIsLoading2(false);
      });
  };

  // --- EFFECT: Triggered only when dates actually change ---
  useEffect(() => {
    if (tabInitialDate && tabFinalDate) {
      getSalesByConsultantSummary(tabInitialDate, tabFinalDate);
      getCancellationsByConsultantSummary(tabInitialDate, tabFinalDate);
    }
  }, [tabInitialDate, tabFinalDate]);

  // --- LOGIC: Prepare Data for BarChart ---
  // We use useMemo here so BarChartData isn't recalculated on every toggle of the UI
  const barChartDataArray = useMemo(() => {
    const data = [];
    salesByConsultantsList.forEach((consultant) => {
      const canx = cancellationsByConsultantsList.find(
        (item) => item.CONSULTANT_ID === consultant.CONSULTANT_ID
      );
      
      const netSales = canx 
        ? consultant.subTotal - canx.subTotal 
        : consultant.subTotal;

      const formattedName = (matches3
        ? consultant.Firstname.charAt(0).toUpperCase()
        : consultant.Firstname.charAt(0).toUpperCase() + consultant.Firstname.toLowerCase().slice(1)) +
        " " + consultant.Lastname.charAt(0);

      data.push({
        Name: formattedName,
        NetSales: netSales,
        Sales: netSales.toLocaleString("en-US", { minimumFractionDigits: 2 }),
      });
    });
    return data;
  }, [salesByConsultantsList, cancellationsByConsultantsList, matches3]);

  return (
    <Fragment>
      <Button
        onClick={showSalesGraphHandler}
        sx={{ color: "#7A1125", textTransform: "capitalize" }}
      >
        <TimelineIcon sx={{ fontSize: 20, color: "#4E1721", marginRight: 2 }} />
        {showSalesGraph ? "Hide Graph" : "Show Graph"}
      </Button>

      {showSalesGraph ? (
        !isLoading1 && !isLoading2 ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: matches ? "column" : "row",
              alignItems: "center",
            }}
          >
            <Grid item xs={12} md={12} lg={8}>
              {salesByConsultantsList.length === 0 ? (
                <EqualizerIcon sx={{ fontSize: 20, color: "#4E1721", marginRight: 2, display: "flex" }} />
              ) : (
                <BarChart BarChartData={barChartDataArray} />
              )}
            </Grid>

            <Grid item xs={12} md={12} lg={4}>
              <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "flex-start" }}>
                <List>
                  {salesByConsultantsList.map((consultant) => {
                    const canx = cancellationsByConsultantsList.find(
                      (item) => item.CONSULTANT_ID === consultant.CONSULTANT_ID
                    );
                    const netSales = canx ? consultant.subTotal - canx.subTotal : consultant.subTotal;

                    return (
                      <Fragment key={consultant.CONSULTANT_ID}>
                        <ListItem sx={{ width: matches ? "100%" : 300 }}>
                          <ListItemAvatar>
                            <Avatar sx={{ bgcolor: "#e7dddd" }}>
                              <AccountCircleIcon sx={{ fontSize: 30, color: "#4E1721", mr: 1 }} />
                            </Avatar>
                          </ListItemAvatar>
                          <MyListItemText
                            primary={`${consultant.Firstname.charAt(0).toUpperCase()}${consultant.Firstname.toLowerCase().slice(1)} ${consultant.Lastname.charAt(0)} : £ ${netSales.toLocaleString("en-US", { minimumFractionDigits: 2 })}`}
                          />
                        </ListItem>
                        <Divider sx={{ width: "80%" }} />
                      </Fragment>
                    );
                  })}
                </List>
              </Box>
            </Grid>
          </Box>
        ) : (
          <Box textAlign="center" sx={{ p: 3 }}>
            <SyncLoader color="#7A1125" size={15} />
          </Box>
        )
      ) : null}
    </Fragment>
  );
}

// CRITICAL: React.memo prevents the component from re-rendering (and re-fetching) 
// unless the dates actually change, even if MainContent re-renders.
export default React.memo(SalesByConsultantSummaryGraph, (prevProps, nextProps) => {
  return (
    prevProps.tabInitialDate === nextProps.tabInitialDate &&
    prevProps.tabFinalDate === nextProps.tabFinalDate
  );
});